import {
  Button,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  ModalHeader,
  Text,
  Tooltip,
  useClipboard,
  useToast,
} from '@chakra-ui/react';
import { RouterLink } from '@gamma/ui/navigation';
import FrameInspectSharpIcon from '@gamma/ui/icons/FrameInspectSharpIcon';
import MoreHorizSharpIcon from '@gamma/ui/icons/MoreHorizSharpIcon';
import CloseSharpIcon from '@gamma/ui/icons/CloseSharpIcon';
import ContentCopySharpIcon from '@gamma/ui/icons/ContentCopySharpIcon';
import ListAltSharpIcon from '@gamma/ui/icons/ListAltSharpIcon';
import { ROUTES } from '@investigator/shared/constants';
import { useLogScaleEntityQuery } from '../../../investigations/hooks';
import { i18n } from '@investigator/shared/localization';
import { useContext, useEffect } from 'react';
import { isPublicIPAddress, getEntityModalTitle } from '../../utils';
import { ExternalIPLinksMenu } from '../ExternalIPLinksMenu';
import { AuthContext } from '@investigator/shared/providers';
import { useEntitiesContext } from '../../context';
import { SuppressEntityMenuItem } from '@investigator/shared/components/SuppressEntityMenuItem';

interface EntityAnalysisModalHeaderProps {
  entityName?: string;
  onClose?: () => void;
}

export const EntityAnalysisModalHeader = ({
  entityName,
  onClose,
}: EntityAnalysisModalHeaderProps) => {
  const modalCopy = i18n.pages.entities.entityAnalysisModal;
  const { copied, copyValue, viewDetections, controls } = i18n.pages.detections;
  const toast = useToast();
  const { hasCopied, onCopy } = useClipboard(entityName || '');

  // Get user role and context
  const { userRole } = useContext(AuthContext);
  const {
    selectedEntity,
    suppressionModal: { onOpen: onSuppressOpen },
    suppression,
  } = useEntitiesContext();

  // Use normalized dates from selectedEntity (context handles fallback to global dates)
  const selectedEntityStart = selectedEntity?.start ?? 0;
  const selectedEntityEnd = selectedEntity?.end ?? 0;

  const isSuppressed = selectedEntity?.entityIsSuppressed || false;
  const suppressionLoading = suppression.loading || suppression.pendingUpdate;
  const isButtonDisabled = !suppression.enabled;

  const VIEW_DETECTIONS_LINK = {
    pathname: ROUTES.detections,
    search: `start=${selectedEntityStart}&end=${selectedEntityEnd}&source_name=${entityName}`,
  };

  // Generate log scale entity query
  const logScaleEntityQuery = useLogScaleEntityQuery({
    entityName,
  });

  // Compute modal title
  const modalTitle = getEntityModalTitle(
    entityName,
    modalCopy.title,
    modalCopy.titleFallback,
  );

  // Check if entity is a public IP for external links
  const isPublicIP = isPublicIPAddress(entityName);

  useEffect(() => {
    if (hasCopied) {
      toast({
        position: 'bottom-right',
        status: 'success',
        description: copied,
      });
    }
  }, [hasCopied, toast, copied]);

  return (
    <ModalHeader pt={5}>
      <HStack justifyContent="space-between" alignItems="center">
        <Text textStyle="h4">{modalTitle}</Text>
        <HStack spacing={2}>
          <Button
            as={RouterLink}
            to={{
              pathname: ROUTES.logSearch,
              search: logScaleEntityQuery,
            }}
            target="_blank"
            variant="solid"
            colorScheme="gray"
            size="sm"
            leftIcon={<FrameInspectSharpIcon />}
            isDisabled={!logScaleEntityQuery}
          >
            {modalCopy.actions.viewIpLogs}
          </Button>
          <Menu>
            <Tooltip label={modalCopy.actions.moreActions}>
              <MenuButton
                as={IconButton}
                variant="solid"
                colorScheme="gray"
                size="sm"
                isLoading={suppressionLoading}
                icon={<MoreHorizSharpIcon />}
                aria-label={modalCopy.actions.moreActions}
              />
            </Tooltip>
            <MenuList>
              {userRole !== 'viewer' && (
                <SuppressEntityMenuItem
                  isSuppressed={isSuppressed}
                  isLoading={suppressionLoading}
                  isDisabled={isButtonDisabled}
                  onClick={onSuppressOpen}
                  suppressText={controls.suppressEntity}
                  unsuppressText={controls.unSuppressEntity}
                />
              )}
              <MenuItem
                as={RouterLink}
                onClick={onClose}
                icon={<ListAltSharpIcon />}
                to={VIEW_DETECTIONS_LINK}
              >
                {viewDetections}
              </MenuItem>
              <MenuItem onClick={onCopy} icon={<ContentCopySharpIcon />}>
                {copyValue}
              </MenuItem>
              {isPublicIP && entityName && (
                <ExternalIPLinksMenu
                  entityName={entityName}
                  useMenuGroup={false}
                />
              )}
            </MenuList>
          </Menu>
          <IconButton
            variant="ghost"
            colorScheme="gray"
            size="sm"
            icon={<CloseSharpIcon />}
            aria-label="Close"
            onClick={onClose}
          />
        </HStack>
      </HStack>
    </ModalHeader>
  );
};
